import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Users, Package, FileText, TrendingUp, Globe, MapPin, Clock,
  ChevronRight, Send, Shield, Star, ArrowRight, Briefcase, Edit
} from 'lucide-react';
import { agentApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: d } = await agentApi.getDashboard();
        setData(d);
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-main py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const profile = data?.profile || {};
  const products = data?.represented_products || [];
  const rfqs = data?.matching_rfqs || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="container-main py-8" data-testid="agent-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-[#0A192F] tracking-tight">Agent Dashboard</h1>
              <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">Agent</Badge>
            </div>
            <p className="text-slate-500 text-sm">Welcome, {user?.name}</p>
          </div>
          <Button onClick={() => navigate('/products')} className="bg-[#0A192F] hover:bg-[#172A46] rounded-sm mt-4 md:mt-0" data-testid="agent-browse-products">
            <Package className="w-4 h-4 mr-2" /> Browse Products to Represent
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Products Represented', value: stats.products || 0, icon: Package },
            { label: 'Suppliers Connected', value: stats.suppliers || 0, icon: Users },
            { label: 'Leads Generated', value: stats.leads || 0, icon: TrendingUp },
          ].map((stat, i) => (
            <div key={i} className="bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className="w-4 h-4 text-[#EA580C]" />
                <span className="text-xs text-slate-500 font-medium">{stat.label}</span>
              </div>
              <p className="text-2xl font-bold text-[#0A192F]">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Profile Card */}
        <div className="bg-white border border-slate-200 p-6 mb-8">
          <div className="flex items-start justify-between mb-4">
            <h2 className="font-semibold text-[#0A192F]">Agent Profile</h2>
            <Badge className={profile.is_verified ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-500"}>
              {profile.is_verified ? 'Verified' : 'Pending Verification'}
            </Badge>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-xs mb-0.5">Country</p>
              <p className="font-medium text-[#0A192F] flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.country || user?.country || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs mb-0.5">Categories</p>
              <div className="flex flex-wrap gap-1">
                {(profile.categories || []).map((c, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">{c}</Badge>
                ))}
                {(!profile.categories || profile.categories.length === 0) && <span className="text-slate-400 text-xs">Not set</span>}
              </div>
            </div>
            <div>
              <p className="text-slate-500 text-xs mb-0.5">Regions</p>
              <div className="flex flex-wrap gap-1">
                {(profile.regions || []).map((r, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">{r}</Badge>
                ))}
                {(!profile.regions || profile.regions.length === 0) && <span className="text-slate-400 text-xs">Not set</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Matching RFQs */}
          <div className="bg-white border border-slate-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-[#0A192F]">Matching Buy Offers</h2>
              <Link to="/rfqs" className="text-xs text-[#EA580C] hover:underline font-medium">View all</Link>
            </div>
            {rfqs.length > 0 ? (
              <div className="divide-y">
                {rfqs.slice(0, 5).map((rfq) => (
                  <Link key={rfq.rfq_id} to={`/rfqs/${rfq.rfq_id}`} className="p-4 flex items-start gap-3 hover:bg-slate-50 block">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#0A192F] text-sm truncate">{rfq.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Buyer from {rfq.buyer_country} &middot; {rfq.quantity} {rfq.quantity_unit}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No matching RFQs found. Update your categories.</p>
              </div>
            )}
          </div>

          {/* Represented Products */}
          <div className="bg-white border border-slate-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-[#0A192F]">Represented Products</h2>
            </div>
            {products.length > 0 ? (
              <div className="divide-y">
                {products.map((p) => (
                  <Link key={p.product_id} to={`/products/${p.product_id}`} className="p-4 flex items-center gap-3 hover:bg-slate-50 block">
                    <div className="w-12 h-12 bg-slate-100 shrink-0 overflow-hidden">
                      {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-slate-300 m-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#0A192F] text-sm truncate">{p.title}</h3>
                      <p className="text-xs text-slate-500">${p.price_min?.toFixed(2)} - ${p.price_max?.toFixed(2)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 mb-3">No products represented yet</p>
                <Button onClick={() => navigate('/products')} size="sm" className="rounded-sm">Browse Products</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
